'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Language, defaultLanguage, supportedLanguages, getTranslation } from '@/lib/translations'
import { applyLanguageToUrl, getPathLanguage, getSupportedLanguage, LANGUAGE_COOKIE, resolvePreferredLanguage } from '@/lib/locale-routing'

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: (key: string) => string
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

interface LanguageProviderProps {
  children: ReactNode
}

const persistLanguagePreference = (language: Language) => {
  localStorage.setItem(LANGUAGE_COOKIE, language)
  document.cookie = `${LANGUAGE_COOKIE}=${language}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`
}

const syncLanguagePath = (lang: Language) => {
  const url = new URL(window.location.href)
  applyLanguageToUrl(url, lang)

  const nextUrl = url.toString()
  if (nextUrl !== window.location.href) {
    window.history.replaceState({}, '', nextUrl)
  }
}

export const LanguageProvider: React.FC<{ children: React.ReactNode; initialLanguage?: Language }> = ({
  children,
  initialLanguage = defaultLanguage,
}) => {
  const [language, setLanguageState] = useState<Language>(initialLanguage)

  // Detect language on mount and periodically check URL
  useEffect(() => {
    const detectLanguage = (): { language: Language; source: 'url' | 'storage' | 'browser' | 'default' } => {
      const pathLanguage = getPathLanguage(window.location.pathname)
      if (pathLanguage) {
        return { language: pathLanguage, source: 'url' }
      }

      // Check cookie/localStorage preference
      const cookieMatch = document.cookie.match(new RegExp(`(?:^|; )${LANGUAGE_COOKIE}=([^;]+)`))
      const cookieLanguage = getSupportedLanguage(cookieMatch?.[1])
      if (cookieLanguage) {
        return { language: cookieLanguage, source: 'storage' }
      }

      const stored = getSupportedLanguage(localStorage.getItem(LANGUAGE_COOKIE))
      if (stored && supportedLanguages.includes(stored)) {
        return { language: stored, source: 'storage' }
      }

      // Check browser language preferences in order
      const browserLanguages = Array.from(
        new Set([...(navigator.languages || []), navigator.language].filter(Boolean))
      )
      const browserLanguage = resolvePreferredLanguage(browserLanguages)
      if (browserLanguage !== defaultLanguage) {
        return { language: browserLanguage, source: 'browser' }
      }

      return { language: defaultLanguage, source: 'default' }
    }

    const updateLanguage = () => {
      const detected = detectLanguage()
      persistLanguagePreference(detected.language)

      if (detected.source !== 'url') {
        syncLanguagePath(detected.language)
      }

      setLanguageState(prevLang => {
        if (prevLang !== detected.language) {
          return detected.language
        }
        return prevLang
      })
    }

    // Set initial language
    updateLanguage()

    // Check URL every second for changes (when user manually changes URL)
    const interval = setInterval(updateLanguage, 1000)

    // Listen for URL changes (back/forward navigation, etc.)
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', updateLanguage)
      return () => {
        clearInterval(interval)
        window.removeEventListener('popstate', updateLanguage)
      }
    }

    return () => clearInterval(interval)
  }, [])

  // Update html lang attribute
  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.documentElement.lang = language
    }
  }, [language])

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    persistLanguagePreference(lang)
    
    // Update the locale prefix without reloading the current page.
    if (typeof window !== 'undefined') {
      syncLanguagePath(lang)
    }
  }

  // Translation function
  const t = (key: string): string => {
    return getTranslation(language, key)
  }

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}